import dayjs from "dayjs";
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { GraphInterval } from "../pageResponse.dto";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

type DateTypes = string | number | Date | dayjs.Dayjs;

export abstract class DateUtil {

  private static readonly OFFSET_BROWSER = dayjs().utcOffset() * 60 * 1000;
  private static _offset = 0;

  static readonly DATE_FORMAT = 'YYYY-MM-DD';
  static readonly TIME_FORMAT = 'HH:mm';
  static readonly DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';

  /**
   * Target timezone offset in milliseconds minus client browser timezone offset
   */
  static get OFFSET() {
    return DateUtil._offset;
  }

  static setOffset(timezone: string) {
    dayjs.tz.setDefault(timezone);
    const offsetTarget = dayjs().tz().utcOffset() * 60 * 1000;
    DateUtil._offset = offsetTarget - DateUtil.OFFSET_BROWSER;
  }

  /**
   * Returns date formatted as "YYYY-MM-DD HH:mm:ss" string
   * @param date date to format
   * @param seconds false to skip seconds
   * @returns
   */
  static getDateISO(date?: DateTypes, seconds = true) {
    return dayjs(date).tz().format(`YYYY-MM-DD HH:mm${seconds ? ':ss' : ''}`);
  }

  /**
   * Returns date formatted as 'DD-MMM HH:mm'
   * @param date date to format
   * @returns
   */
  static getShortDateTime(date?: DateTypes, seconds = false) {
    return dayjs(date).tz().format(`DD-MMM HH:mm${seconds ? ':ss' : ''}`);
  }

  /**
   * Returns date formatted as 'MMM DD, YYYY'
   * @param date date to format
   * @returns
   */
  static getShortDate(date?: DateTypes) {
    return dayjs(date).tz().format('MMM DD, YYYY');
  }

  /**
   * Returns unix time in seconds formatted as 'MMM DD YYYY HH:mm:ss' string
   * @param unixTimeSec unix time in seconds
   * @returns
   */
  static convertUnixToHuman(unixTimeSec: number) {
    return dayjs(unixTimeSec * 1000).tz().format('MMM DD YYYY HH:mm:ss');
  }

  /**
  * Get daily graph start/end from now in seconds
  * @returns
  */
  static initShort() {
    const endShort = Date.now() / 1000;
    const startDay = new Date((endShort - 60 * 60 * 24 * 1) * 1000).getTime() / 1000;
    return { endShort, startDay };
  }

  /**
   * Get week, month, year graph start/end from now in seconds
   * @returns
   */
  static initLong() {
    const endLong = Date.now() / 1000;
    const startWeek = new Date((endLong - 60 * 60 * 24 * 7) * 1000).getTime() / 1000;
    const startMonth = new Date((endLong - 60 * 60 * 24 * 30) * 1000).getTime() / 1000;
    const startYear = new Date((endLong - 60 * 60 * 24 * 365) * 1000).getTime() / 1000;
    return { endLong, startWeek, startMonth, startYear };
  }

  /**
   * Get start/end time in seconds
   * @param interval graph interval
   * @returns
   */
  static getRange(interval: GraphInterval): { start: number; end: number; } {
    if (interval === 'day') {
      const short = DateUtil.initShort();
      return { start: short.startDay, end: short.endShort };
    } else {
      const long = DateUtil.initLong();
      switch (interval) {
        case 'week':
          return { start: long.startWeek, end: long.endLong };
        case 'month':
          return { start: long.startMonth, end: long.endLong };
        case 'year':
          return { start: long.startYear, end: long.endLong };
      }
    }
  }
}